import type { MyIpDraftSnapshot, MyIpItem, MyIpProfile } from './types';
import { getMyIpMediaUrl, getMyIpMediaResourceKey } from './mediaUrl';
import {
  canAcceptMyIpMoreMaterials, isMyIpMoreMaterialResource, MyIpMaterialLimitError,
} from './materialLimits';
import { getMyIpResourceKey, materialToMyIpResource, upsertMyIpResource } from './resourceSlots';

interface MaterialSync extends Record<string, unknown> {
  schema_version: 1;
  additions: unknown[];
  removed: string[];
}

/** 未知素材原样保留，只有明确的对象结构才参与身份匹配。 */
export function getMyIpMaterialObject(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
}

/** 资产身份只接受服务端字符串或安全整数，绝不从地址或消息 ID 伪造。 */
function assetId(value: unknown): string | undefined {
  if (typeof value === 'string' && value) return value;
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? String(value) : undefined;
}

/** 本地去重/删除键保留真实地址（含签名），并同时记录已有资产身份。 */
export function getMyIpMaterialKeys(value: unknown): string[] {
  const entry = getMyIpMaterialObject(value);
  const asset = getMyIpMaterialObject(entry?.asset) ?? entry;
  const id = assetId(asset?.asset_id ?? asset?.assetId);
  const url = getMyIpMediaUrl(asset?.url);
  return [...(id ? [`asset:${id}`] : []), ...(url ? [`url:${url}`, `media:${getMyIpMediaResourceKey(url)}`] : [])];
}

/** 历史资源关系也服从已保存的显式删除，不将其重新投影成无地址卡片。 */
export function isMyIpMaterialRemoved(profile: MyIpProfile, value: unknown): boolean {
  const sync = readSync(profile);
  return getMyIpMaterialKeys(value).some((key) => sync?.removed.includes(key));
}

/** 读取未知版本安全降级；仅在要修改时拒绝覆盖未来客户端的删除记录。 */
function readSync(profile: MyIpProfile, writing = false): MaterialSync | undefined {
  const value = profile.material_sync;
  if (value == null) return undefined;
  const sync = getMyIpMaterialObject(value);
  if (sync?.schema_version !== 1 || !Array.isArray(sync.additions)
    || !Array.isArray(sync.removed) || sync.removed.some((key) => typeof key !== 'string')) {
    if (writing) throw new Error('Unsupported IP material synchronization state');
    return undefined;
  }
  return sync as MaterialSync;
}

/** 主视觉和三视图只在对应槽位展示，不重复作为更多素材。 */
export function isMyIpPrimaryMaterial(profile: MyIpProfile, value: unknown): boolean {
  const entry = getMyIpMaterialObject(value);
  const asset = getMyIpMaterialObject(entry?.asset) ?? entry;
  const url = getMyIpMediaUrl(asset?.url);
  return Boolean(url && [profile.main_visual, profile.turnaround_visual].includes(url));
}

/** 实时补充优先保持到达顺序；同一素材合并 metadata，未知对象不丢弃。 */
function mergeMaterials(additions: unknown[], materials: unknown[], preferFirst = false): unknown[] {
  const merged = [...additions];
  materials.forEach((material) => {
    const keys = getMyIpMaterialKeys(material);
    const index = keys.length ? merged.findIndex((entry) => getMyIpMaterialKeys(entry).some((key) => keys.includes(key))) : -1;
    if (index < 0) merged.push(material);
    else {
      const previous = getMyIpMaterialObject(preferFirst ? material : merged[index]);
      const next = getMyIpMaterialObject(preferFirst ? merged[index] : material);
      merged[index] = {
        ...previous,
        ...next,
        ...(previous?.asset && next?.asset ? { asset: { ...getMyIpMaterialObject(previous.asset), ...getMyIpMaterialObject(next.asset) } } : {}),
      };
    }
  });
  return merged;
}

/** 失败重读时只重放本地素材操作；服务端新扩展键和其它设定保持权威。 */
export function mergeMyIpMaterialSync(base: MyIpProfile, pending: MyIpProfile): MyIpProfile {
  const remote = readSync(base, true);
  const local = readSync(pending, true);
  if (!local) return base;
  return applyMyIpMaterialSync({
    ...base,
    material_sync: {
      ...local,
      ...remote,
      schema_version: 1,
      additions: mergeMaterials(local.additions, remote?.additions || [], true),
      removed: Array.from(new Set([...(remote?.removed || []), ...local.removed])),
    },
  });
}

/** 将持久化补充集和删除标识落实到完整 materials；重进页面和旧摘要均使用同一规则。 */
export function applyMyIpMaterialSync(profile: MyIpProfile): MyIpProfile {
  const sync = readSync(profile);
  if (profile.material_sync != null && !sync) return profile;
  if (!Array.isArray(profile.materials)) return profile;
  const materials = mergeMaterials(sync?.additions || [], profile.materials).filter((entry) => (
    !getMyIpMaterialKeys(entry).some((key) => sync?.removed.includes(key))
  ));
  return { ...profile, materials };
}

/** 完整 Agent 摘要不拥有前端覆盖层；保留授权主档中的字段，避免旧摘要复活已删除素材。 */
export function mergeMyIpAgentMaterials(base: MyIpProfile, next: MyIpProfile): MyIpProfile {
  readSync(base, true);
  const { material_sync: ignoredSync, ...agent } = next;
  return applyMyIpMaterialSync({ ...base, ...agent });
}

/** 实时媒体直接归集到 resources，历史 profile 媒体数组不参与槽位真源。 */
export function getMyIpMediaResultSnapshot(item: MyIpItem, raw: unknown, message?: unknown): MyIpDraftSnapshot | undefined {
  const event = getMyIpMaterialObject(raw);
  if (event?.type !== 'media_result') return undefined;
  const previous = readSync(item.profile, true);
  const normalized = getMyIpMaterialObject(getMyIpMaterialObject(message)?.mediaInfo)?.list;
  const rawItems: unknown[] = Array.isArray(event.media_items) ? event.media_items : [];
  const incoming = Array.isArray(normalized) ? normalized : rawItems;
  let { resources } = item;
  const primary = [item.coverAssetId, item.coverUrl, ...item.resources.filter((entry) => entry.role === 'three_view').map((entry) => entry.url)]
    .map(getMyIpMediaResourceKey).filter(Boolean);
  for (const value of incoming) {
    const media = getMyIpMaterialObject(value);
    if (!media) continue; // eslint-disable-line no-continue
    const url = getMyIpMediaUrl(media.url || media.media_url || media.ori_media_url);
    const original = getMyIpMaterialObject(rawItems.find((entry) => getMyIpMediaUrl(getMyIpMaterialObject(entry)?.media_url) === url));
    const candidate = materialToMyIpResource({
      ...original,
      ...media,
      url,
      type: media.type || media.media_type || event.media_type,
      name: media.name || original?.name || event.title,
      thumbnail_url: Array.isArray(normalized) ? media.poster || '' : media.media_cover_url,
      media_url_by_water_mark: Array.isArray(normalized) ? media.watermarkUrl || '' : media.media_url_by_water_mark,
      media_cover_url_by_water_mark: Array.isArray(normalized) ? media.watermarkCoverUrl || '' : media.media_cover_url_by_water_mark,
    });
    if (!candidate || primary.includes(getMyIpMediaResourceKey(url))
      || getMyIpMaterialKeys({ ...candidate, asset_id: candidate.assetId }).some((key) => previous?.removed.includes(key))) continue; // eslint-disable-line no-continue
    const known = resources.some((entry) => entry.role === candidate.role && getMyIpResourceKey(entry) === getMyIpMediaResourceKey(candidate.url || candidate.assetId));
    // 新更多素材超 100 时整批拒绝本次写入，避免半批入库；已有同键刷新仍允许。
    if (!known && isMyIpMoreMaterialResource({ ...candidate, status: candidate.status ?? 'active' })
      && !canAcceptMyIpMoreMaterials({ resources }, 1)) {
      throw new MyIpMaterialLimitError();
    }
    resources = upsertMyIpResource(resources, candidate);
    if (!known && candidate.role === 'additional_image') {
      const added = resources[resources.length - 1];
      resources = [added, ...resources.slice(0, -1)];
    }
  }
  let imageOrder = 0;
  resources = resources.map((entry) => {
    if (entry.role !== 'additional_image') return entry;
    const ordered = { ...entry, sortOrder: imageOrder };
    imageOrder += 1;
    return ordered;
  });
  if (JSON.stringify(resources) === JSON.stringify(item.resources)) return undefined;
  return {
    name: item.name, summary: item.summary, coverAssetId: item.coverAssetId, profile: item.profile, resources,
  };
}

/** 删除按钮只移除更多素材；即使 URL 相同，也保留三视图和音色的独立关系。 */
export function getMyIpMaterialRemovalSnapshot(item: MyIpItem, keys: string[]): MyIpDraftSnapshot {
  const previous = readSync(item.profile, true);
  const removed = Array.from(new Set([...(previous?.removed || []), ...keys]));
  return {
    name: item.name,
    summary: item.summary,
    coverAssetId: item.coverAssetId,
    profile: {
      ...item.profile,
      material_sync: {
        ...previous, schema_version: 1, additions: previous?.additions || [], removed,
      },
    },
    resources: item.resources.filter((resource) => !['additional_image', 'video'].includes(resource.role)
      || (!getMyIpMaterialKeys(resource).some((key) => removed.includes(key))
        && !removed.includes(`media:${getMyIpResourceKey(resource)}`))),
  };
}
